import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../../state/auth.js';
import { translateError } from '../../i18n.js';
import { reloadPlan } from '../../utils/plan.js';
import Card from './Card.jsx';
import Button from './Button.jsx';

// Instellingen › Sportcomplex: "wij zijn gehuisvest op complex X". The complex approves;
// from then on reports on its places go to the complex's staff instead of to the club's
// accommodatiezaken.

const ERRORS = {
  invalid_complex: 'Kies een sportcomplex.',
  already_linked: 'Jullie club is al aan een ander sportcomplex gekoppeld. Beëindig dat eerst.',
  invalid_contacts: 'Kies actieve leden van de club (maximaal 20).',
};
const message = err => ERRORS[err.code] || translateError(err.code) || 'Dat lukte niet.';

export default function ComplexLinkCard() {
  const [state, setState] = useState(null);
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { api('/api/complexes/club').then(setState).catch(() => {}); }, []);

  // relock: the link started or ended, so Storingen may open or lock (src/utils/plan.js)
  async function run(fn, { relock = false } = {}) {
    setBusy(true); setError(null);
    try { setState(await fn()); setChoice(''); if (relock) reloadPlan(); } catch (err) { setError(message(err)); }
    setBusy(false);
  }
  const saveContacts = ids => run(() => api('/api/complexes/club/contacts', { method: 'PUT', body: JSON.stringify({ user_ids: ids }) }));
  const request = () => run(() => api('/api/complexes/club', { method: 'POST', body: JSON.stringify({ complex_id: Number(choice) }) }), { relock: true });
  const end = () => confirm(state.link.status === 'actief'
    ? `De koppeling met ${state.link.complex_name} beëindigen? Jullie leden kunnen dan geen storingen op het complex meer melden in de app. Meldingen die nog open staan, blijven bij het complex.`
    : `De aanvraag bij ${state.link.complex_name} intrekken?`)
    && run(() => api('/api/complexes/club', { method: 'DELETE' }), { relock: true });

  // No complex on Vereniging4U yet: nothing to choose, so no card
  if (!state || (!state.link && !state.complexes.length)) return null;
  const { link } = state;
  return (
    <Card title="Sportcomplex" style="margin-top:24px">
      {!link && (
        <>
          <p class="text-sm" style="margin:0 0 10px">
            Spelen jullie op een sportcomplex dat Vereniging4U gebruikt? Koppel jullie club, dan gaan storingen aan velden,
            kleedkamers en andere plekken van het complex rechtstreeks naar de beheerder van het complex. Storingen in jullie
            eigen kantine of clubhuis blijven bij jullie accommodatiezaken.
          </p>
          <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center">
            <select class="form-input" style="flex:1 1 220px;width:auto" value={choice} onChange={e => setChoice(e.target.value)} aria-label="Sportcomplex">
              <option value="">Kies het sportcomplex…</option>
              {state.complexes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <Button disabled={busy || !choice} onClick={request}>Wij zijn hier gehuisvest</Button>
          </div>
        </>
      )}
      {link?.status === 'aangevraagd' && (
        <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center">
          <span class="text-sm" style="flex:1 1 220px">Aangevraagd bij <strong>{link.complex_name}</strong>. De beheerder van het complex moet de koppeling nog goedkeuren; je krijgt een melding.</span>
          <Button variant="secondary" size="sm" disabled={busy} onClick={end}>Aanvraag intrekken</Button>
        </div>
      )}
      {link?.status === 'actief' && (
        <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center">
          <span class="text-sm" style="flex:1 1 220px">Gekoppeld aan <strong>{link.complex_name}</strong>. Storingen op het complex gaan naar de beheerder van het complex.</span>
          <Button variant="secondary" size="sm" disabled={busy} onClick={end}>Koppeling beëindigen</Button>
        </div>
      )}
      {link && <Contacts state={state} busy={busy} onSave={saveContacts} />}
      {error && <div class="alert alert-error" style="margin-top:10px">{error}</div>}
    </Card>
  );
}

// The contactpersonen: besides the club admins, who gets what the complex shares with
// the besturen (mededelingen, documenten, later the gebruikersoverleg)
function Contacts({ state, busy, onSave }) {
  const [pick, setPick] = useState('');
  const ids = state.contacts.map(c => c.id);
  const choices = state.members.filter(m => !ids.includes(m.id));
  return (
    <div style="margin-top:14px;padding-top:12px;border-top:1px solid var(--color-border)">
      <div style="font-weight:600;font-size:14px;margin-bottom:4px">Contactpersonen voor het complex</div>
      <div class="text-sm text-muted" style="margin-bottom:8px">De clubbeheerders krijgen de berichten en documenten voor besturen al. Wijs hier extra contactpersonen aan, bijvoorbeeld het bestuurslid accommodatie.</div>
      {state.contacts.map(c => (
        <div key={c.id} style="display:flex;align-items:center;gap:8px;padding:4px 0;font-size:14px">
          <span style="flex:1">{c.display_name}</span>
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => onSave(ids.filter(id => id !== c.id))}>Weghalen</Button>
        </div>
      ))}
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:6px">
        <select class="form-input" style="flex:1 1 200px;width:auto" value={pick} onChange={e => setPick(e.target.value)} aria-label="Contactpersoon toevoegen">
          <option value="">Kies een lid…</option>
          {choices.map(m => <option key={m.id} value={m.id}>{m.display_name}</option>)}
        </select>
        <Button size="sm" disabled={busy || !pick} onClick={() => { onSave([...ids, Number(pick)]); setPick(''); }}>Toevoegen</Button>
      </div>
    </div>
  );
}
